import React from "react";
import CssBaseline from "@mui/material/CssBaseline";
import {createTheme, ThemeProvider} from "@mui/material/styles";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { BRAND, BRAND_FONTS } from '@gnothi/shared/utils/brand'

// Brand constants (values from `@gnothi/shared/utils/brand`) — single definition point for non-MUI consumers (Amplify auth theme, splash gradients) that can't read `theme.vars.*`. Import these instead of re-hardcoding the hex. #00A8E8 is the primary brand accent; the navy is a dark-chrome surface (background.elevated), not a brand color.
// The `_RAMP`/`_LIGHT` stops exist so the splash surfaces below can spell themselves in NAMES: every hex the marketing pages render is defined in this block, once, and a page that wants to tint a band has to come here first.
export const BRAND_ACCENT = BRAND.accent
export const BRAND_ACCENT_RAMP = BRAND.accentRamp
export const BRAND_ACCENT_LIGHT = BRAND.accentLight
export const BRAND_NAVY = BRAND.navy
export const BRAND_NAVY_RAMP = BRAND.navyRamp
export const SURFACE_PAPER = BRAND.paper
export const SURFACE_DEFAULT = BRAND.surface

/** The `dark` band wash — `Static/Section color="dark"` is its only consumer, and it reads this rather than owning a hex of its own. Note it is SCHEME-INVARIANT: a `dark` Band is navy in the light and the dark color scheme alike, which is why the `splash.*` tokens below need an `onDark` cell that does NOT flip with the scheme. */
export const DARK_BAND_BACKGROUND = `radial-gradient(circle at top, ${BRAND_NAVY_RAMP} 0%, ${BRAND_NAVY} 70%)`

// TOKEN-ESCAPE: raw hex for non-MUI renderers (react-icons fills, chart libs) that can't take theme.vars refs. Mirrored into palette.feature below for sx access. This is the sanctioned escape hatch — see the entry.* tokens in the taxonomy for the MUI-side equivalent.
export const featureColors = {
  prompt: '#50627a',
  behavior: '#507a6f',
  organization: '#696B38',
  themes: '#59547a',
} satisfies FeaturePalette

/** The same four tints lightened for a DARK surface, each ≥4.5:1 on `BRAND_NAVY_RAMP` — the gradient's inner stop, which is the hardest of the four dark surfaces and also exactly where a `SectionHeader` glyph lands. The base set above is 4.6–7.1:1 on white but only 2.0–2.9:1 there, so a glyph or bullet rendered on a `dark` Band (or on any surface in the dark color scheme) must read from HERE. Wired both ways below: as the dark scheme's `feature`, and as `featureOnDark` in both schemes for the scheme-invariant navy band. Both sets take `satisfies FeaturePalette` (mobile's theme/index.ts uses the same guard) because `FeatureTone` is `keyof typeof featureColors`: a fifth tone added THERE but not here would type-check while `featureOnDark.<tone>` resolved to nothing — a glyph with no color on a dark band, which typecheck, the DOM suite and an artifact grep are all blind to. */
export const featureColorsOnDark = {
  prompt: '#8894a5',
  behavior: '#7a9a92',
  organization: '#959672',
  themes: '#9592aa',
} satisfies FeaturePalette

// Affective/domain palette extension. NAMES are palette roles only (no
// health/wellness/mood copy). entry.mood* are the issue's affective roles;
// entry.chat/chapter/story/shareTag are the domain accents the Entries list
// and sharing surfaces consume via theme.vars.palette.entry.*.
type EntryPalette = {
  moodPositive: string; moodNegative: string; neutral: string
  chat: string; chapter: string; story: string; shareTag: string
}
type FeaturePalette = { prompt: string; behavior: string; organization: string; themes: string }

/**
 * The marketing pages' accent, as the 2×2 it actually is: color SCHEME × band TONE.
 * `primary.main` matches `accent` in both schemes but has no on-navy-band cell, and a raw
 * `BRAND_ACCENT_RAMP` cannot serve either, because a literal hex is scheme-BLIND: `#0077c2` is 4.75:1 on white but 3.8:1 once `background.paper` flips to
 * `#0f172a` in the dark scheme. These three tokens put the scheme axis where MUI already
 * solves it (one CSS var per scheme) and leave `splash-sections`' `accentColor(onDark)`
 * holding only the tone axis. Every cell is ≥4.5:1 — see that helper's docstring.
 *   `accent`       accent text/glyph on `background.paper` or `.default` (a `light`/`grey` Band)
 *   `accentOnDark` the same role on the scheme-invariant navy wash of a `dark` Band
 *   `onAccent`     text or a glyph laid ON an `accent` FILL (the pair, not a color)
 *   `onAccentOnDark` the label ON an `accentOnDark` FILL — navy in both schemes (6.9:1 / 8.7:1); `heroCta` in splash-sections is its one consumer
 */
type SplashPalette = { accent: string; accentOnDark: string; onAccent: string; onAccentOnDark: string }

declare module '@mui/material/styles' {
  interface Palette { feature: FeaturePalette; featureOnDark: FeaturePalette; splash: SplashPalette; entry: EntryPalette }
  // REQUIRED, not optional, even though MUI's own option types are all-optional: `Palette` above declares these four non-optional, so an omitted one is typed present and `undefined` at runtime — an `sx` path that silently resolves to nothing. Required here makes the two `colorSchemes` palettes below the enforcement point, so adding a third scheme (or dropping a key from one) is a compile error instead. Sound because `createTheme` is called exactly once in this program.
  interface PaletteOptions { feature: FeaturePalette; featureOnDark: FeaturePalette; splash: SplashPalette; entry: EntryPalette }
  interface TypeBackground { elevated: string }
  // The theme below sets `cssVariables`, so `theme.vars` always exists
  // at runtime. Without this augmentation MUI types `theme.vars` as optional
  // (`Partial<Pick<CssVarsTheme,'vars'>>`), which makes every `theme.vars.*`
  // callsite a TS18048 ("possibly undefined") error — blocking the tokenized
  // design-system remap that docs/gnothi/web.md directs new code toward.
  interface CssThemeVariables { enabled: true }
}

// Gradient button styles - splash/marketing pages only, not in-app
export const splashGradient = {
  primary: {
    background: `linear-gradient(135deg, ${BRAND_NAVY} 0%, ${BRAND_NAVY_RAMP} 100%)`,
    boxShadow: '0 4px 14px 0 rgba(0,0,0,0.1)',
    '&:hover': {
      background: `linear-gradient(135deg, ${BRAND_NAVY_RAMP} 0%, ${BRAND_NAVY} 100%)`,
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
    },
  },
} as const

export const theme = createTheme({
  // colorSchemeSelector:'class' is required for setMode() to work. Without it,
  // MUI defaults to 'media' (prefers-color-scheme) when both colorSchemes are
  // present, which ignores setMode() calls entirely. 'class' makes MUI toggle
  // a CSS class on <html>. MUI persists/restores the choice itself via
  // localStorage 'mui-mode' — AccountMenu reads/writes it through useColorScheme().
  cssVariables: { colorSchemeSelector: 'class' },
  colorSchemes: {
    light: {
      palette: {
        // `secondary` keeps the SAME blue as `primary` so the ~40 deferred
        // `color="secondary"`/`secondary.main` callsites stay blue until they're
        // re-pointed to primary.
        // `main` is the RAMP, not `BRAND_ACCENT`: `#00A8E8` is 2.7:1 against white both as text on paper and as white text on a fill, while `#0077c2` is 4.75:1 (4.54:1 on `background.default`) both ways, so every primary link, icon, contained button, chip and badge clears AA from this one entry.
        primary: { main: BRAND_ACCENT_RAMP, light: BRAND_ACCENT, contrastText: SURFACE_PAPER },
        secondary: { main: BRAND_ACCENT_RAMP, light: BRAND_ACCENT, contrastText: SURFACE_PAPER },
        background: { paper: SURFACE_PAPER, default: SURFACE_DEFAULT, elevated: BRAND_NAVY },
        text: { primary: '#0f172a', secondary: '#475569', disabled: '#94a3b8' },
        divider: 'rgba(226, 232, 240, 0.8)',
        // Status `main`s are each hue's darker ramp stop so they read as text on paper (≥4.6:1 on `background.default`, white on them ≥4.8:1); the brighter stops stay as `light`.
        error: { main: '#dc2626', light: '#ef4444' },
        warning: { main: '#b45309', light: '#f59e0b' },
        success: { main: '#047857', light: '#10b981' },
        feature: featureColors,
        featureOnDark: featureColorsOnDark,
        splash: { accent: BRAND_ACCENT_RAMP, accentOnDark: BRAND_ACCENT, onAccent: SURFACE_PAPER, onAccentOnDark: BRAND_NAVY },
        entry: {
          moodPositive: '#10b981', moodNegative: '#ef4444', neutral: '#9E9E9E',
          chat: '#00796b', chapter: '#6A1B9A', story: '#6A1B9A', shareTag: '#7B1FA2',
        },
      },
    },
    dark: {
      palette: {
        // `contrastText` is NAVY here, which is why nothing may paint it onto the (scheme-invariant, navy) `dark` Band — `Static/Section` and the splash heroes use `common.white`. It is correct only ON a `primary.main` FILL, which in this scheme is the light `#38bdf8`.
        primary: { main: BRAND_ACCENT_LIGHT, light: '#7dd3fc', contrastText: BRAND_NAVY },
        secondary: { main: BRAND_ACCENT_LIGHT, light: '#7dd3fc', contrastText: BRAND_NAVY },
        background: { paper: '#0f172a', default: '#0a0f1d', elevated: '#1e293b' },
        text: { primary: '#f1f5f9', secondary: '#94a3b8', disabled: '#64748b' },
        divider: 'rgba(148, 163, 184, 0.2)',
        error: { main: '#f87171' },
        warning: { main: '#fbbf24' },
        success: { main: '#34d399' },
        feature: featureColorsOnDark,
        featureOnDark: featureColorsOnDark,
        splash: { accent: BRAND_ACCENT_LIGHT, accentOnDark: BRAND_ACCENT_LIGHT, onAccent: BRAND_NAVY, onAccentOnDark: BRAND_NAVY },
        entry: {
          moodPositive: '#34d399', moodNegative: '#f87171', neutral: '#9E9E9E',
          chat: '#4db6ac', chapter: '#ba68c8', story: '#ba68c8', shareTag: '#ce93d8',
        },
      },
    },
  },
  typography: {
    fontFamily: BRAND_FONTS.body,
    h1: {
      fontWeight: 700,
      fontFamily: BRAND_FONTS.heading,
      fontSize: '4.5rem',
      lineHeight: '1.1',
      marginBottom: '1rem',
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 600,
      fontFamily: BRAND_FONTS.heading,
      fontSize: '3rem',
      lineHeight: '1.2',
      marginBottom: '1rem',
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 600,
      fontFamily: BRAND_FONTS.heading,
      fontSize: '2rem',
      lineHeight: '1.3',
    },
    h4: {
      fontWeight: 400,
      fontFamily: BRAND_FONTS.body,
      fontSize: '1.5rem',
      lineHeight: '1.5',
      marginBottom: '1rem',
    },
    h5: {
      fontWeight: 500,
      fontFamily: BRAND_FONTS.heading,
      lineHeight: '1.3',
      fontSize: "1.5rem"
    },
    h6: {
      fontWeight: 600,
      fontFamily: BRAND_FONTS.body,
      lineHeight: '1.3',
      fontSize: '1.125rem',
    },
    subtitle1: {
      fontFamily: BRAND_FONTS.body,
      fontSize: '1.25rem',
      fontWeight: '400',
    },
    subtitle2: {
      fontFamily: BRAND_FONTS.heading,
      fontSize: '1.125rem',
      fontWeight: '500',
    },
    body1: {
      fontFamily: BRAND_FONTS.body,
      fontWeight: '400',
      fontSize: "1rem",
      lineHeight: '1.6',
    },
    body2: {
      fontFamily: BRAND_FONTS.body,
      fontWeight: '400',
      fontSize: "0.875rem",
      lineHeight: '1.6',
    },
    caption: {
      fontFamily: BRAND_FONTS.body,
    },
    overline: {
      fontFamily: BRAND_FONTS.body,
      fontWeight: '600',
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
    },
    button: {
      fontFamily: "Inter",
      fontSize: '0.875rem',
      fontWeight: '500',
    },
  },
  components: {
    MuiButton: {
      defaultProps: {
        color: 'primary' as const,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 4,
          transition: 'all 0.2s ease-in-out',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({theme}) => ({
          backgroundColor: theme.vars.palette.background.paper,
          borderRadius: 4,
          boxShadow: '0 4px 14px -4px rgba(0,0,0,0.06)',
          transition: 'all 0.3s ease-in-out',
          border: `1px solid ${theme.vars.palette.divider}`,
        }),
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: ({theme}) => ({
          backgroundColor: theme.vars.palette.background.paper,
          borderRadius: '4px !important',
        }),
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: ({theme}) => ({
          fontSize: '0.6875rem',
          fontWeight: 600,
          fontFamily: BRAND_FONTS.body,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          // `text.secondary`, not `.disabled`: `#94a3b8` is 2.56:1 on white.
          color: theme.vars.palette.text.secondary,
          lineHeight: '1.5',
          paddingTop: '16px',
          paddingBottom: '4px',
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 4,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small' as const,
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: 'small' as const,
      },
    },
    MuiButtonGroup: {
      defaultProps: {
        size: 'small' as const,
      },
    },
  },
})

/** `className` for content laid on `background.elevated` (the navy app bar, Fields toolbar, Fields stats strip). That band is dark in BOTH schemes, so in the light scheme the default `text.*`, `action.active` and `primary.main` land dark-on-navy (about 1.05:1). The class re-scopes the subtree to the dark scheme's CSS vars, so stock MUI controls pick their on-dark colors with no per-control overrides. Put it INSIDE the band, never on the element that paints `background.elevated`, or the band itself takes the dark scheme's slate. Menus and dialogs portal out of it and keep the page scheme. */
export const ON_ELEVATED = 'dark'

export default function Mui({children}: React.PropsWithChildren<{}>) {
  return <LocalizationProvider dateAdapter={AdapterDayjs}>
    <ThemeProvider theme={theme} defaultMode="light">
      <CssBaseline />
      {children}
    </ThemeProvider>
  </LocalizationProvider>
}
